import { SubscriptionTier } from '@/types';

// Re-export from the new component for backward compatibility
export const ATTACK_SURFACE_OPTIONS = [
  {
    label: 'External',
    value: 'external',
  },
  {
    label: 'Web',
    value: 'applications',
  },
  {
    label: 'Code',
    value: 'code',
  },
  {
    label: 'Internal',
    value: 'internal',
  },
  {
    label: 'Cloud',
    value: 'cloud',
  },
  {
    label: 'Users',
    value: 'users',
  },
  {
    label: 'Device',
    value: 'device',
  },
];

export const SUBSCRIPTION_TIERS: SubscriptionTier[] = [
  {
    id: 'freemium',
    displayName: 'Freemium',
    features: {
      'Product Modules': {
        'Attack Surface Management': true,
        'Unified Vulnerability Management': true,
        'Continuous Penetration Testing': false,
        'Cyber Threat Intelligence': false,
        'Application Security Testing': false,
        'Adversary Emulation Validation': false,
      },
    },
  },
  {
    id: 'foundational',
    displayName: 'Foundational',
    features: {
      'Product Modules': {
        'Attack Surface Management': true,
        'Unified Vulnerability Management': true,
        'Continuous Penetration Testing': true,
        'Cyber Threat Intelligence': true,
        'Application Security Testing': false,
        'Adversary Emulation Validation': false,
      },
    },
  },
  {
    id: 'professional',
    displayName: 'Enterprise',
    features: {
      'Product Modules': {
        'Attack Surface Management': true,
        'Unified Vulnerability Management': true,
        'Continuous Penetration Testing': true,
        'Cyber Threat Intelligence': true,
        'Application Security Testing': true,
        'Adversary Emulation Validation': false,
      },
    },
  },
  {
    id: 'ultimate',
    displayName: 'Ultimate',
    features: {
      'Product Modules': {
        'Attack Surface Management': true,
        'Unified Vulnerability Management': true,
        'Continuous Penetration Testing': true,
        'Cyber Threat Intelligence': true,
        'Application Security Testing': true,
        'Adversary Emulation Validation': true,
      },
    },
  },
];

export const NO_OF_ASSETS_OPTIONS = [
  { value: 5000, label: '≤ 5K' },
  { value: 25000, label: '≤ 25K' },
  { value: 50000, label: '≤ 50K' },
  { value: 100000, label: '≤ 100K' },
  { value: -1, label: '> 100K (Contact Sales)' },
];

export const AI_CREDIT_PACKS = [
  { id: 'none', label: 'BYOAI', creditsPerMonth: 0, annualPrice: 0 },
  { id: 'starter', label: 'Starter', creditsPerMonth: 500, annualPrice: 6000 },
  {
    id: 'analyst',
    label: 'Analyst',
    creditsPerMonth: 1000,
    annualPrice: 12000,
  },
  {
    id: 'operator',
    label: 'Operator',
    creditsPerMonth: 2500,
    annualPrice: 30000,
  },
  {
    id: 'maestro',
    label: 'Maestro',
    creditsPerMonth: 5000,
    annualPrice: 60000,
  },
] as const;

export type AICreditPackId = (typeof AI_CREDIT_PACKS)[number]['id'];

export const AI_CREDIT_ACTIONS = [
  {
    action: 'Analyst Query',
    description:
      'AI interprets, analyzes, and responds to natural language questions',
    credits: 2,
  },
  {
    action: 'Vulnerability Triage',
    description:
      'AI analyzes vulnerability severity, exploitability, context, priority',
    credits: 4,
  },
  {
    action: 'Remediation Guidance',
    description: 'AI generates specific fix with code/config changes',
    credits: 8,
  },
  {
    action: 'Executive Summary',
    description: 'AI generates an executive summary',
    credits: 10,
  },
  {
    action: 'Report Generation',
    description: 'AI generates comprehensive risk report',
    credits: 50,
  },
  {
    action: 'Code Analysis',
    description: 'AI reviews code PR requests',
    credits: 100,
  },
  {
    action: 'Single-Stage Attack',
    description: 'AI conducts single-turn attack against a specific target',
    credits: 500,
  },
  {
    action: 'Multi-Stage Attack',
    description: 'AI simulates multi-step attack / exploitation chain',
    credits: 1000,
  },
  {
    action: 'Comprehensive Code Analysis',
    description:
      'AI identifies latent risks through threat modeling, fuzzing, and exploitation development',
    credits: 2500,
  },
] as const;

export const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

/** Shared Tailwind class for subscription form inputs */
export const SUBSCRIPTION_INPUT_CLASS =
  'border-chariot-stroke focus:border-chariot-brand-primary focus:ring-chariot-brand-primary bg-chariot-background-primary text-chariot-text-primary w-50 rounded-md border-2 px-4 py-2 transition-all focus:ring-2 focus:ring-offset-1 focus:outline-hidden';

/** Scan frequency type shared across subscription components */
export type ScanFrequency = 'daily' | 'weekly' | 'monthly' | 'quarterly';
